import { isSafeLinkUrl, parseRich, RICH_SIZE_EM, type RichBlock, type RichSize, type RichSpan } from '@survey/core';
import { Fragment, type CSSProperties, type ReactNode } from 'react';

// Rich question text (see packages/core/src/rich.ts) as React elements. The
// markup is parsed by us and turned into elements one by one, never into HTML,
// so nothing a questionnaire author or respondent types can run as code.

function spanNode(s: RichSpan, key: number): ReactNode {
  let node: ReactNode = s.text.split('\n').map((line, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {line}
    </Fragment>
  ));
  if (s.bold) node = <strong>{node}</strong>;
  if (s.italic) node = <em>{node}</em>;
  if (s.underline) node = <u>{node}</u>;
  if (s.href && isSafeLinkUrl(s.href)) {
    node = (
      <a href={s.href} target="_blank" rel="noopener noreferrer">
        {node}
      </a>
    );
  }
  return <Fragment key={key}>{node}</Fragment>;
}

const spans = (list: RichSpan[]) => list.map(spanNode);

/**
 * The blocks inside a label or heading: each paragraph or list item on its own line, with space
 * between paragraphs and each one's own alignment (spans shown as blocks are valid there).
 */
function inlineNodes(blocks: RichBlock[]): ReactNode[] {
  return blocks.map((b, i) => {
    const style: CSSProperties = { display: 'block', ...(i > 0 ? { marginTop: '0.6em' } : {}), ...blockStyle(b) };
    if (b.kind === 'space') return <span key={i} className="rich-space" aria-hidden style={{ display: 'block', height: '1em', marginTop: '0.6em' }} />;
    if (b.kind === 'p') return <span key={i} className="rich-line" style={style}>{spans(b.spans)}</span>;
    return (
      <span key={i} className="rich-line" style={style}>
        {b.items.map((item, j) => (
          <span key={j} style={{ display: 'block' }}>
            {`${b.kind === 'ul' ? '•' : `${j + 1}.`} `}
            {spans(item)}
          </span>
        ))}
      </span>
    );
  });
}

/**
 * `inline` renders everything as one run of text, fit for a heading or label;
 * otherwise paragraphs and lists become real blocks.
 */
export function RichText({ source, inline = false, className }: { source: string; inline?: boolean; className?: string }) {
  const blocks = parseRich(source);
  if (inline) return <span className={className}>{inlineNodes(blocks)}</span>;
  return (
    <div className={`rich${className ? ` ${className}` : ''}`}>
      {blocks.map((b, i) =>
        b.kind === 'space' ? (
          <p key={i} className="rich-space" aria-hidden />
        ) : b.kind === 'p' ? (
          <p key={i} style={blockStyle(b)}>{spans(b.spans)}</p>
        ) : b.kind === 'ul' ? (
          <ul key={i} style={blockStyle(b)}>
            {b.items.map((item, j) => (
              <li key={j}>{spans(item)}</li>
            ))}
          </ul>
        ) : (
          <ol key={i} style={blockStyle(b)}>
            {b.items.map((item, j) => (
              <li key={j}>{spans(item)}</li>
            ))}
          </ol>
        ),
      )}
    </div>
  );
}

/** The alignment and size of one paragraph or list. */
export function blockStyle(b: { align?: string; size?: RichSize }): CSSProperties | undefined {
  const a = alignStyle(b.align);
  if (!b.size) return a;
  return { ...a, fontSize: RICH_SIZE_EM[b.size] };
}

/** The paragraph alignment a question asks for. */
export function alignStyle(align: string | undefined): CSSProperties | undefined {
  if (align === 'left') return { textAlign: 'left' };
  if (align === 'center' || align === 'right') return { textAlign: align };
  // A word that doesn't fit moves to the next line whole: no hyphen is added and the word is never cut.
  if (align === 'justify') return { textAlign: 'justify', hyphens: 'none', WebkitHyphens: 'none', overflowWrap: 'normal', wordBreak: 'normal' };
  return undefined;
}
